import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  CreditCard, Plus, Edit3, Trash2, Search, CalendarClock, Settings,
  IndianRupee, AlertCircle, TrendingUp, Loader2, PlayCircle, History,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Active: 'border-emerald-500/40 text-emerald-300',
  Paused: 'border-amber-500/40 text-amber-300',
  Cancelled: 'border-slate-500/40 text-slate-400',
};

function fINR(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: v < 100 ? 2 : 0 })}`;
}
function fDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) { return iso; }
}
function daysBetween(iso) {
  if (!iso) return null;
  const t = new Date(iso + 'T00:00:00');
  const n = new Date(); n.setHours(0, 0, 0, 0);
  return Math.round((t - n) / 86400000);
}

const EMPTY_FORM = {
  name: '', category: 'Other', vendor: '', currency: 'INR', amount: 0,
  billing_cycle: 'Monthly', custom_usd_rate: '', start_date: '',
  auto_renew: true, payment_note: '', notes: '',
};

export default function SubscriptionsPage() {
  const { isOwner, hasPerm } = useAuth();
  // Reviewers can view; only finance_admin (or Owner/Admin via bypass) can edit.
  const canView = hasPerm('finance_reviewer');
  const canManage = hasPerm('finance_admin');

  const [meta, setMeta] = useState({ categories: [], cycles: [], statuses: [], currencies: [] });
  const [subs, setSubs] = useState([]);
  const [summary, setSummary] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  const [settings, setSettings] = useState({ usd_to_inr_rate: 88, renewal_reminder_days_before: [3, 1] });
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState('');
  const [fCat, setFCat] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fCurrency, setFCurrency] = useState('all');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ usd_to_inr_rate: 88, days_text: '3, 1' });

  const [logOpen, setLogOpen] = useState(null); // subscription object being viewed
  const [logRows, setLogRows] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fCat !== 'all') params.category = fCat;
      if (fStatus !== 'all') params.status = fStatus;
      if (fCurrency !== 'all') params.currency = fCurrency;
      const [listRes, upRes] = await Promise.all([
        api.get('/subscriptions', { params }),
        api.get('/subscriptions/upcoming', { params: { days: 30 } }),
      ]);
      setSubs(listRes.data?.subscriptions || []);
      setSummary(listRes.data?.summary || {});
      setUpcoming(upRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get('/subscriptions/meta').then((r) => setMeta(r.data)).catch(() => {});
    api.get('/finance-settings').then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [fCat, fStatus, fCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return subs;
    return subs.filter((s) => `${s.name} ${s.vendor || ''} ${s.category}`.toLowerCase().includes(term));
  }, [subs, q]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM, start_date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '', category: s.category || 'Other', vendor: s.vendor || '',
      currency: s.currency || 'INR', amount: s.amount || 0,
      billing_cycle: s.billing_cycle || 'Monthly',
      custom_usd_rate: s.custom_usd_rate ?? '',
      start_date: s.start_date || '',
      auto_renew: !!s.auto_renew, payment_note: s.payment_note || '', notes: s.notes || '',
    });
    setOpen(true);
  };

  const inrPreview = useMemo(() => {
    const amt = Number(form.amount || 0);
    if (form.currency !== 'USD' || !amt) return null;
    const rate = Number(form.custom_usd_rate) > 0 ? Number(form.custom_usd_rate) : Number(settings.usd_to_inr_rate || 88);
    return amt * rate;
  }, [form, settings]);

  const canSave = form.name.trim() && form.start_date && Number(form.amount) >= 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        vendor: form.vendor || null,
        currency: form.currency,
        amount: Number(form.amount) || 0,
        billing_cycle: form.billing_cycle,
        custom_usd_rate: form.custom_usd_rate === '' ? null : Number(form.custom_usd_rate),
        start_date: form.start_date,
        auto_renew: !!form.auto_renew,
        payment_note: form.payment_note || null,
        notes: form.notes || '',
      };
      if (editing) await api.patch(`/subscriptions/${editing.id}`, payload);
      else await api.post('/subscriptions', payload);
      setOpen(false);
      toast.success(editing ? 'Subscription updated.' : 'Subscription added.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!confirm(`Delete "${s.name}"? Its payment history will remain in the ledger.`)) return;
    try { await api.delete(`/subscriptions/${s.id}`); toast.success('Deleted.'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggleStatus = async (s, next) => {
    try { await api.patch(`/subscriptions/${s.id}`, { status: next }); load(); }
    catch (e) { toast.error('Could not update status'); }
  };

  const openSettings = () => {
    setSettingsForm({
      usd_to_inr_rate: settings.usd_to_inr_rate || 88,
      days_text: (settings.renewal_reminder_days_before || [3, 1]).join(', '),
    });
    setSettingsOpen(true);
  };
  const saveSettings = async () => {
    try {
      const days = String(settingsForm.days_text || '')
        .split(',').map((x) => parseInt(x.trim(), 10)).filter((x) => Number.isFinite(x) && x >= 0);
      const payload = { usd_to_inr_rate: Number(settingsForm.usd_to_inr_rate) };
      if (days.length) payload.renewal_reminder_days_before = days;
      const res = await api.patch('/finance-settings', payload);
      setSettings(res.data);
      setSettingsOpen(false);
      toast.success('Finance settings updated.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const runJob = async () => {
    try {
      const res = await api.post('/subscriptions/run-daily-job');
      const { rolled_over, reminders_sent } = res.data || {};
      toast.success(`Job ran — ${rolled_over || 0} rolled over, ${reminders_sent || 0} reminders sent.`);
      load();
    } catch (e) { toast.error('Could not run job'); }
  };

  const openLog = async (s) => {
    setLogOpen(s);
    try {
      const res = await api.get('/subscriptions/payment-log', { params: { subscription_id: s.id } });
      setLogRows(res.data?.entries || []);
    } catch (_) { setLogRows([]); }
  };

  if (!canView) {
    return (
      <div>
        <PageHeader icon={CreditCard} title="Subscriptions" subtitle="Finance access required." />
        <Card className="shine-card p-6 text-center text-slate-400 rounded-xl">
          You don&apos;t have permission to view finance data. Ask Admin to grant Finance-Reviewer.
        </Card>
      </div>
    );
  }

  const totalActive = summary.count_active ?? 0;
  const rate = summary.usd_to_inr_rate ?? settings.usd_to_inr_rate ?? 88;

  return (
    <div>
      <PageHeader
        icon={CreditCard}
        title="Subscriptions"
        subtitle={`Track recurring spend across ${totalActive} active service${totalActive === 1 ? '' : 's'} · USD→INR @ ₹${Number(rate).toFixed(2)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canManage && (
              <>
                <Button onClick={runJob} variant="outline" className="border-sky-500/40 text-sky-200 hover:bg-sky-500/10" data-testid="sub-run-job">
                  <PlayCircle className="h-4 w-4 mr-1.5" /> Run rollover job
                </Button>
                <Button onClick={openSettings} variant="outline" className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10" data-testid="sub-open-settings">
                  <Settings className="h-4 w-4 mr-1.5" /> Finance settings
                </Button>
                <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="sub-new-btn">
                  <Plus className="h-4 w-4 mr-1" /> New subscription
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-amber-300/70 font-semibold">Daily spend</div>
          <div className="text-2xl font-extrabold shine-gold-text mt-1" data-testid="sub-total-daily">{fINR(summary.total_daily_inr)}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-amber-300/70 font-semibold">Monthly spend</div>
          <div className="text-2xl font-extrabold shine-gold-text mt-1" data-testid="sub-total-monthly">{fINR(summary.total_monthly_inr)}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-amber-300/70 font-semibold">Yearly spend</div>
          <div className="text-2xl font-extrabold shine-gold-text mt-1" data-testid="sub-total-yearly">{fINR(summary.total_yearly_inr)}</div>
          {summary.total_one_time_this_year_inr > 0 && (
            <div className="text-[11px] text-slate-400 mt-1">+ {fINR(summary.total_one_time_this_year_inr)} one-time this year</div>
          )}
        </Card>
      </div>

      {/* Upcoming renewals */}
      <Card className="shine-card p-4 rounded-xl mb-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock className="h-4 w-4 text-amber-300" />
          <h3 className="font-bold text-white text-sm uppercase tracking-wider">Upcoming renewals · next 30 days</h3>
        </div>
        {upcoming.length === 0 ? (
          <div className="text-sm text-slate-400 italic">Nothing renewing in the next 30 days.</div>
        ) : (
          <div className="space-y-1.5">
            {upcoming.map((s) => {
              const dl = daysBetween(s.next_renewal_date);
              const dlColor = dl <= 1 ? 'bg-red-500/20 text-red-300 border-red-500/40'
                : dl <= 7 ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
                : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40';
              return (
                <div key={s.id} className="flex items-center gap-3 bg-[#0f2240] border border-amber-500/15 rounded-md px-3 py-2">
                  <Badge variant="outline" className={`text-[10px] ${dlColor} shrink-0`}>{dl <= 0 ? 'Today' : `${dl}d`}</Badge>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate font-semibold">{s.name}</div>
                    <div className="text-[11px] text-slate-400">{fDate(s.next_renewal_date)} · {s.category}</div>
                  </div>
                  <div className="text-sm text-amber-200 font-mono shrink-0">{fINR(s.inr_amount)}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Filters */}
      <Card className="shine-card p-3 rounded-xl mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300/70" />
          <Input placeholder="Search name / vendor…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-[#0f2240] border-amber-500/20 text-white" data-testid="sub-search" />
        </div>
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger className="w-40 bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20">
            <SelectItem value="all">All categories</SelectItem>
            {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-36 bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20">
            <SelectItem value="all">All statuses</SelectItem>
            {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCurrency} onValueChange={setFCurrency}>
          <SelectTrigger className="w-28 bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Currency" /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20">
            <SelectItem value="all">All</SelectItem>
            {meta.currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {/* Table */}
      {loading ? (
        <Card className="shine-card p-10 text-center rounded-xl"><Loader2 className="h-5 w-5 animate-spin mx-auto text-amber-300" /></Card>
      ) : filtered.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">
          {subs.length === 0 ? 'No subscriptions yet. Click “New subscription” to add one.' : 'No matches for your filters.'}
        </Card>
      ) : (
        <Card className="shine-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0f2240] text-slate-300">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Name</th>
                  <th className="text-left px-3 py-2 font-semibold">Category</th>
                  <th className="text-right px-3 py-2 font-semibold">Amount</th>
                  <th className="text-left px-3 py-2 font-semibold">Cycle</th>
                  <th className="text-right px-3 py-2 font-semibold">Monthly (₹)</th>
                  <th className="text-left px-3 py-2 font-semibold">Next renewal</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const dl = daysBetween(s.next_renewal_date);
                  return (
                    <tr key={s.id} className="border-t border-amber-500/10 hover:bg-white/[0.02]" data-testid={`sub-row-${s.id}`}>
                      <td className="px-3 py-2">
                        <div className="text-white font-semibold">{s.name}</div>
                        <div className="text-[11px] text-slate-400">{s.vendor || '—'}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-300">{s.category}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        <div className="text-white">{s.currency} {Number(s.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                        {s.currency === 'USD' && <div className="text-[10px] text-slate-400">≈ {fINR(s.inr_amount)}</div>}
                      </td>
                      <td className="px-3 py-2 text-slate-300">{s.billing_cycle}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-200">{fINR(s.normalized_costs?.monthly)}</td>
                      <td className="px-3 py-2">
                        <div className="text-slate-300">{fDate(s.next_renewal_date)}</div>
                        {dl != null && s.status === 'Active' && s.billing_cycle !== 'One-time' && (
                          <div className={`text-[10px] ${dl <= 3 ? 'text-red-300' : dl <= 7 ? 'text-amber-300' : 'text-slate-500'}`}>
                            {dl <= 0 ? 'Renewing today' : `in ${dl}d`}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" onClick={() => openLog(s)} className="h-7 w-7 text-sky-300 hover:bg-sky-500/10" title="Payment history" data-testid={`sub-log-${s.id}`}><History className="h-3.5 w-3.5" /></Button>
                        {canManage && (
                          <>
                            <Button size="icon" variant="ghost" onClick={() => openEdit(s)} className="h-7 w-7 text-amber-300 hover:bg-amber-500/10" title="Edit" data-testid={`sub-edit-${s.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                            {s.status === 'Active'
                              ? <Button size="icon" variant="ghost" onClick={() => toggleStatus(s, 'Paused')} className="h-7 w-7 text-slate-300 hover:bg-slate-500/10" title="Pause">⏸</Button>
                              : <Button size="icon" variant="ghost" onClick={() => toggleStatus(s, 'Active')} className="h-7 w-7 text-emerald-300 hover:bg-emerald-500/10" title="Reactivate">▶</Button>
                            }
                            <Button size="icon" variant="ghost" onClick={() => remove(s)} className="h-7 w-7 text-red-400 hover:bg-red-500/10" title="Delete" data-testid={`sub-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ==================== ADD / EDIT DIALOG ==================== */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-amber-500/10 shrink-0">
            <DialogTitle className="text-white">{editing ? `Edit · ${editing.name}` : 'New subscription'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 px-6 py-4 min-h-0">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder="e.g. Emergent Pro" data-testid="sub-form-name" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">
                    {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder="Company" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">
                    {meta.currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="sub-form-amount" />
                {inrPreview != null && (
                  <div className="text-[11px] text-emerald-300 mt-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> ≈ {fINR(inrPreview)}
                  </div>
                )}
              </div>
              <div>
                <Label>Billing cycle</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">
                    {meta.cycles.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start date *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="sub-form-start" />
              </div>
              {form.currency === 'USD' && (
                <div className="col-span-2">
                  <Label>Custom USD→INR rate (optional)</Label>
                  <Input type="number" value={form.custom_usd_rate} onChange={(e) => setForm({ ...form, custom_usd_rate: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder={`Uses global rate ₹${settings.usd_to_inr_rate}`} />
                  <div className="text-[11px] text-slate-400 mt-1">Overrides the global rate for this subscription only — useful if you locked in a different rate.</div>
                </div>
              )}
              <div className="col-span-2">
                <Label>Payment note</Label>
                <Input value={form.payment_note} onChange={(e) => setForm({ ...form, payment_note: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder="e.g. HDFC card ending 4521" />
                <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Never store full card numbers.</div>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0f2240] border-amber-500/20" />
              </div>
              <label className="col-span-2 flex items-center gap-2 cursor-pointer">
                <Switch checked={!!form.auto_renew} onCheckedChange={(v) => setForm({ ...form, auto_renew: !!v })} data-testid="sub-form-auto-renew" />
                <span className="text-sm text-slate-200">Auto-renews (informational — rollover happens either way)</span>
              </label>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-amber-500/10 shrink-0 bg-[#0a1628]">
            <Button onClick={save} disabled={!canSave || saving} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="sub-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Add subscription')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== FINANCE SETTINGS DIALOG ==================== */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Finance settings</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Default USD → INR rate</Label>
              <Input type="number" step="0.01" value={settingsForm.usd_to_inr_rate} onChange={(e) => setSettingsForm({ ...settingsForm, usd_to_inr_rate: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="finance-rate" />
              <div className="text-[11px] text-slate-400 mt-1">Applied to all USD subscriptions unless they have a custom override.</div>
            </div>
            <div>
              <Label>Renewal reminder days (comma-separated)</Label>
              <Input value={settingsForm.days_text} onChange={(e) => setSettingsForm({ ...settingsForm, days_text: e.target.value })} className="bg-[#0f2240] border-amber-500/20" placeholder="3, 1" data-testid="finance-days" />
              <div className="text-[11px] text-slate-400 mt-1">Emails Owner + Admin these many days before each renewal (each cycle emailed once).</div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveSettings} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== PAYMENT LOG DIALOG ==================== */}
      <Dialog open={!!logOpen} onOpenChange={(o) => !o && (setLogOpen(null), setLogRows([]))}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">Payment history · {logOpen?.name}</DialogTitle></DialogHeader>
          {logRows.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-4">No payments logged yet. Entries are created automatically when the daily rollover job runs.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0f2240] text-slate-300 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Renewed on</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2">INR at time</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((e) => (
                    <tr key={e.id} className="border-t border-amber-500/10">
                      <td className="px-3 py-2">{fDate(e.renewed_on)}</td>
                      <td className="px-3 py-2 text-right font-mono">{e.currency} {Number(e.amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-200">{fINR(e.inr_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
